/**
 * Google Analytics 4 on the website and the game page, only with the visitor's consent (KVKK
 * and GDPR: analytics cookies need it). Until the visitor accepts, the Google tag is not even
 * loaded; a small banner asks once, and "Cookie settings" in the footer asks again. Ads
 * signals and ad personalisation stay off. Off entirely when the build has no VITE_GA_ID.
 */
import { t } from "../i18n";
import { inApp } from "../native";

const GA_ID = import.meta.env.VITE_GA_ID ?? "";
/** "granted" or "denied": the visitor's answer (nothing yet = ask). */
const KEY = "wc.consent";

type Choice = "granted" | "denied";

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

function readChoice(): Choice | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

function saveChoice(choice: Choice) {
  try {
    localStorage.setItem(KEY, choice);
  } catch {
    // Private mode: the banner asks again on the next page.
  }
}

let loaded = false;
/** Loads the Google tag and starts measuring (once per page). */
function startAnalytics() {
  if (loaded || !GA_ID) return;
  loaded = true;
  window.dataLayer = window.dataLayer ?? [];
  // gtag reads the arguments object itself, as Google's snippet does.
  function gtag(..._args: unknown[]) {
    // biome-ignore lint/complexity/noArguments: the Google tag expects the arguments object
    window.dataLayer?.push(arguments);
  }
  gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  gtag("js", new Date());
  gtag("config", GA_ID, {
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.append(s);
}

/** Removes the cookies the Google tag set (after the visitor withdraws consent). */
function clearAnalyticsCookies() {
  for (const part of document.cookie.split(";")) {
    const name = part.split("=")[0]?.trim() ?? "";
    if (!name.startsWith("_ga")) continue;
    for (const domain of [
      "",
      `; domain=${location.hostname}`,
      `; domain=.${location.hostname.replace(/^www\./, "")}`,
    ])
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
  }
}

let banner: HTMLElement | null = null;

/** Shows the cookie banner (again, from "Cookie settings"). */
export function openConsent() {
  if (!GA_ID) return;
  banner?.remove();
  const box = document.createElement("div");
  box.className = "consent";
  box.setAttribute("role", "dialog");
  box.setAttribute("aria-live", "polite");
  box.setAttribute("aria-label", t("Cookies"));
  const text = document.createElement("p");
  text.textContent = t(
    "We use Google Analytics cookies to see how visitors use the site, only if you allow it. Sign-in cookies are always on because the site needs them.",
  );
  const more = document.createElement("a");
  more.href = "/privacy/";
  more.textContent = t("Privacy Policy");
  const actions = document.createElement("div");
  actions.className = "consent-actions";
  const button = (label: string, cls: string, choice: Choice) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = cls;
    b.textContent = label;
    b.onclick = () => {
      const before = readChoice();
      saveChoice(choice);
      box.remove();
      banner = null;
      if (choice === "granted") startAnalytics();
      // Withdrawing consent: drop the cookies, and reload so the tag stops for good.
      else if (before === "granted") {
        clearAnalyticsCookies();
        location.reload();
      }
    };
    return b;
  };
  actions.append(
    button(t("Reject"), "consent-no", "denied"),
    button(t("Accept"), "consent-yes", "granted"),
  );
  text.append(" ", more);
  box.append(text, actions);
  document.body.append(box);
  banner = box;
}

/** Starts analytics when the visitor agreed before, asks when they have not answered yet. */
export function initConsent() {
  // The iOS app has no Google Analytics (and so no cookie banner): no tracking in the app.
  if (!GA_ID || inApp()) return;
  for (const link of document.querySelectorAll<HTMLElement>("[data-cookie-settings]")) {
    link.hidden = false;
    link.addEventListener("click", (e) => {
      e.preventDefault();
      openConsent();
    });
  }
  const choice = readChoice();
  if (choice === "granted") startAnalytics();
  else if (choice === null) openConsent();
}
